/**
 * DemoPlayerService – Auto-play execution engine.
 *
 * Executes DemoScript steps sequentially with:
 *  - Configurable speed (1x / 2x / 3x)
 *  - Pause / Resume / Stop
 *  - Element highlighting + smooth scroll
 *  - Route navigation
 *  - Click simulation
 *
 * Designed as a singleton class used by DemoControlBar + DemoGuideOverlay.
 */
import type { DemoScriptStep, DemoScript } from './DemoScripts';
import { resolveScriptRoute } from './DemoScripts';
import { demoNarration } from './DemoNarrationService';
import type { DemoSpeed } from '@/modules/shared/contexts/DemoContext';

export type PlayerCallback = {
  onStepChange: (stepIndex: number, step: DemoScriptStep) => void;
  onComplete: () => void;
  onNavigate: (route: string) => void;
};

export class DemoPlayerService {
  private script: DemoScript | null = null;
  private stepIndex = 0;
  private speed: DemoSpeed = 1;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private paused = false;
  private callbacks: PlayerCallback | null = null;
  private programId = '';
  private patientId?: string;

  // ─── Lifecycle ──────────────────────────────────────

  start(
    script: DemoScript,
    programId: string,
    callbacks: PlayerCallback,
    speed: DemoSpeed = 1,
    patientId?: string,
  ): void {
    this.stop();
    this.script = script;
    this.programId = programId;
    this.patientId = patientId;
    this.callbacks = callbacks;
    this.speed = speed;
    this.stepIndex = 0;
    this.running = true;
    this.paused = false;
    void this.executeCurrentStep();
  }

  pause(): void {
    if (!this.running || this.paused) return;
    this.paused = true;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    demoNarration.pause();
  }

  resume(): void {
    if (!this.running || !this.paused) return;
    this.paused = false;
    // Re-run the current step so narration resumes cleanly from the start of
    // this step (browser speech queue cannot be reliably resumed mid-step).
    void this.executeCurrentStep();
  }

  stop(): void {
    this.running = false;
    this.paused = false;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    demoNarration.stop();
    this.script = null;
    this.stepIndex = 0;
  }

  setSpeed(speed: DemoSpeed): void {
    this.speed = speed;
  }

  nextStep(): void {
    if (!this.script || !this.running) return;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    demoNarration.stop();
    this.advanceStep();
  }

  previousStep(): void {
    if (!this.script || !this.running) return;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    demoNarration.stop();
    this.stepIndex = Math.max(0, this.stepIndex - 1);
    void this.executeCurrentStep();
  }

  isRunning(): boolean {
    return this.running;
  }

  isPaused(): boolean {
    return this.paused;
  }

  getCurrentStepIndex(): number {
    return this.stepIndex;
  }

  // ─── Internal ───────────────────────────────────────

  /**
   * Run the current step as a strict sequence:
   *   1. Hard-reset speech engine
   *   2. Notify UI (panel / overlay)
   *   3. Navigate route (if any) and wait for it to settle
   *   4. Wait for the target DOM element (if any) to mount
   *   5. Highlight + scroll target
   *   6. Speak narration AND wait for both narration + min visual delay
   *   7. Advance
   *
   * This prevents the "narration stops at step 3" bug, which was caused by
   * speech being started in parallel with route changes — the route's
   * unmount/mount cycle silently aborted the SpeechSynthesis queue.
   */
  private async executeCurrentStep(): Promise<void> {
    if (!this.script || !this.running || !this.callbacks) return;
    const step = this.script.steps[this.stepIndex];
    if (!step) {
      this.callbacks.onComplete();
      this.running = false;
      return;
    }

    const stepIndexAtStart = this.stepIndex;
    const isCurrent = () =>
      this.running && !this.paused && this.script !== null && this.stepIndex === stepIndexAtStart;

    // 1. Hard reset before every step (fixes Chrome stuck-queue bug).
    demoNarration.hardReset();

    // 2. UI sync.
    this.callbacks.onStepChange(this.stepIndex, step);

    // eslint-disable-next-line no-console
    console.log(`[Demo] Step ${this.stepIndex + 1}: ${step.id} → ${step.title}`);

    // 3. Navigate first (if applicable) and let the route settle.
    if (step.action === 'NAVIGATE' && step.route) {
      const route = resolveScriptRoute(step.route, this.programId, this.patientId);
      if (route) {
        this.callbacks.onNavigate(route);
        await this.delay(700);
      }
    }
    if (!isCurrent()) return;

    // 4. Wait for target DOM element if specified.
    if (step.target) {
      await this.waitForElement(step.target, 6000);
    }
    if (!isCurrent()) return;

    // 5. Highlight + scroll, plus side-effect actions.
    this.performStepSideEffect(step);

    // 6. Speak + min-delay (whichever finishes last wins).
    const minDelay = Math.max(step.delayMs / this.speed, 800);
    const narrationPromise = step.narration
      ? new Promise<void>((resolve) => demoNarration.speak(step.narration!, resolve, step.emotion))
      : Promise.resolve();
    const delayPromise = this.delay(minDelay);

    await Promise.all([narrationPromise, delayPromise]);
    if (!isCurrent()) return;

    // 7. Advance.
    this.advanceStep();
  }

  private performStepSideEffect(step: DemoScriptStep): void {
    switch (step.action) {
      case 'CLICK': {
        if (step.target) {
          setTimeout(() => {
            const el = document.querySelector(step.target!) as HTMLElement | null;
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              setTimeout(() => el.click(), 300);
            }
          }, 200);
        }
        break;
      }
      case 'HIGHLIGHT': {
        if (step.target) {
          const el = document.querySelector(step.target) as HTMLElement | null;
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        break;
      }
      case 'NAVIGATE':
      case 'WAIT':
      case 'FORM_SUBMIT':
      case 'API_CALL':
      case 'WHAT_IF':
        break;
    }
  }

  private waitForElement(selector: string, timeoutMs: number): Promise<HTMLElement | null> {
    return new Promise((resolve) => {
      const start = Date.now();
      const tick = () => {
        if (!this.running || this.paused) {
          resolve(null);
          return;
        }
        const el = document.querySelector(selector) as HTMLElement | null;
        if (el) {
          resolve(el);
          return;
        }
        if (Date.now() - start >= timeoutMs) {
          // eslint-disable-next-line no-console
          console.warn('[Demo] Target not found within timeout:', selector);
          resolve(null);
          return;
        }
        setTimeout(tick, 200);
      };
      tick();
    });
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      this.timer = setTimeout(() => {
        this.timer = null;
        resolve();
      }, ms);
    });
  }

  private advanceStep(): void {
    if (!this.script || !this.running) return;
    this.stepIndex++;
    if (this.stepIndex >= this.script.steps.length) {
      this.running = false;
      this.callbacks?.onComplete();
      return;
    }
    void this.executeCurrentStep();
  }
}

/** Singleton instance for app-wide use. */
export const demoPlayer = new DemoPlayerService();
